import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { employerAPI, candidateAPI, getProxiedLogoUrl } from '../services/api';
import toast from 'react-hot-toast';
import AdminDashboard from './dashboards/AdminDashboard';
import EmployerDashboard from './dashboards/EmployerDashboard';
import CandidateDashboard from './dashboards/CandidateDashboard';
import CandidateCoursePage from './CandidateCoursePage';
import NotificationBell from './NotificationBell';
import ProductWalkthrough from './ProductWalkthrough';
import DashboardSidebar from './DashboardSidebar';
import UserMenu from './UserMenu';

const NavIcon = ({ d }) => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
  </svg>
);

const NAV_ICON_PATHS = {
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  prescreening: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  assessments: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  reports: 'M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  jobs: 'M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m8 0h-8m8 0v2a2 2 0 01-2 2H8a2 2 0 01-2-2V6',
  courses: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253',
  profile: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z',
  tour: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

const Dashboard = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const courseRouteActive = location.pathname.includes('/dashboard/candidate/course/');
  
  // Candidate navigation state (used in header)
  const [candidateView, setCandidateView] = useState('dashboard');
  const [prescreeningStatus, setPrescreeningStatus] = useState({
    hasResume: false,
    isCompleted: false,
    isLoading: true
  });
  const [isTakingAssessment, setIsTakingAssessment] = useState(false);
  const [candidateAction, setCandidateAction] = useState(null);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [walkthroughIsReplay, setWalkthroughIsReplay] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  
  // Employer profile state (used in header)
  const [employerProfile, setEmployerProfile] = useState(null);

  const apiBaseUrl = useMemo(
    () => (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, ''),
    []
  );

  const refreshEmployerProfile = useCallback(async () => {
    if (user?.userType !== 'employer' && user?.userType !== 'employer_staff') {
      return;
    }
    try {
      const response = await employerAPI.getProfile();
      setEmployerProfile(response.data);
    } catch (error) {
      console.error('Error fetching employer profile:', error);
      toast.error('Failed to load employer profile. Please try again later.');
    }
  }, [user]);

  useEffect(() => {
    refreshEmployerProfile();
  }, [refreshEmployerProfile]);

  const closeWalkthrough = async ({ dontShow = false, feedback = null } = {}) => {
    setShowWalkthrough(false);
    if (walkthroughIsReplay) {
      // A tour the user asked to re-watch must not overwrite first-run preferences.
      setWalkthroughIsReplay(false);
      return;
    }
    try {
      const payload = {
        walkthroughCompleted: true,
        walkthroughDontShow: dontShow,
      };
      if (feedback) {
        payload.walkthroughFeedback = feedback;
        payload.walkthroughFeedbackAt = new Date().toISOString();
      }
      await candidateAPI.updatePreferences(payload);
    } catch (error) {
      // Local dismiss is enough to avoid blocking the dashboard.
    }
  };

  const leaveNestedCourseRoute = useCallback(() => {
    if (location.pathname !== '/dashboard') {
      navigate('/dashboard');
    }
  }, [location.pathname, navigate]);

  const goToCandidateView = (view) => {
    setCandidateAction(null);
    setCandidateView(view);
    leaveNestedCourseRoute();
  };

  const replayWalkthrough = () => {
    // The tour highlights elements that only exist on the dashboard view.
    goToCandidateView('dashboard');
    setWalkthroughIsReplay(true);
    setShowWalkthrough(true);
  };

  const openCandidateProfile = () => {
    goToCandidateView('dashboard');
    setCandidateAction('profile');
  };

  const handleCandidateNotification = (item) => {
    const view = item?.ctaView;
    if (view === 'prescreening') {
      goToCandidateView('prescreening');
      return;
    }
    if (view === 'profile' || view === 'continue-assessment') {
      setCandidateView('dashboard');
      setCandidateAction(view);
      leaveNestedCourseRoute();
      return;
    }
    if (view === 'assessments' || view === 'reports' || view === 'jobs' || view === 'courses') {
      if (view === 'assessments' && !prescreeningStatus.isCompleted) {
        goToCandidateView('prescreening');
        return;
      }
      goToCandidateView(view);
    }
  };

  const employerLogoUrl = useMemo(() => {
    const rawLogo =
      employerProfile?.companyLogoUrl ||
      user?.companyLogoUrl ||
      employerProfile?.company_logo_url ||
      user?.company_logo_url;
    return getProxiedLogoUrl(rawLogo, apiBaseUrl);
  }, [apiBaseUrl, employerProfile, user]);

  const normalizedUserType = useMemo(() => {
    const rawType = user?.userType;
    if (!rawType) return undefined;
    return rawType === 'employer_staff' ? 'employer' : rawType;
  }, [user?.userType]);

  const candidateNavVisible = normalizedUserType === 'candidate' && !isTakingAssessment;

  useEffect(() => {
    if (!candidateNavVisible) setMobileNavOpen(false);
  }, [candidateNavVisible]);

  const candidateNavItems = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard', dataTour: 'dashboard', icon: <NavIcon d={NAV_ICON_PATHS.home} /> },
    {
      id: 'prescreening',
      label: 'Prescreening',
      dataTour: 'prescreening',
      icon: <NavIcon d={NAV_ICON_PATHS.prescreening} />,
      complete: prescreeningStatus.isCompleted,
    },
    {
      id: 'assessments',
      label: 'Assessments',
      dataTour: 'assessments',
      icon: <NavIcon d={NAV_ICON_PATHS.assessments} />,
      locked: !prescreeningStatus.isCompleted,
      lockedHint: prescreeningStatus.hasResume
        ? 'Complete prescreening to unlock skill assessments'
        : 'Upload your resume, then complete prescreening to unlock assessments',
    },
    { id: 'courses', label: 'Courses', dataTour: 'courses', icon: <NavIcon d={NAV_ICON_PATHS.courses} /> },
    { id: 'jobs', label: 'Jobs', dataTour: 'jobs', icon: <NavIcon d={NAV_ICON_PATHS.jobs} /> },
    { id: 'reports', label: 'Reports', dataTour: 'reports', icon: <NavIcon d={NAV_ICON_PATHS.reports} /> },
    { id: 'tour', label: 'View product tour', icon: <NavIcon d={NAV_ICON_PATHS.tour} /> },
  ], [prescreeningStatus.isCompleted, prescreeningStatus.hasResume]);

  const candidateActiveNavId = useMemo(() => {
    if (courseRouteActive) return 'courses';
    if (candidateView === 'assessments' && !prescreeningStatus.isCompleted) return null;
    return candidateView;
  }, [courseRouteActive, candidateView, prescreeningStatus.isCompleted]);

  const candidateProfileNavItem = useMemo(() => ({
    id: 'profile',
    label: 'Edit Profile',
    dataTour: 'profile',
    accent: true,
    icon: <NavIcon d={NAV_ICON_PATHS.profile} />,
  }), []);

  const handleCandidateNavSelect = (item) => {
    if (item.id === 'profile') {
      openCandidateProfile();
      return;
    }
    if (item.id === 'tour') {
      replayWalkthrough();
      return;
    }
    if (item.locked) {
      toast(item.lockedHint, { icon: '🔒' });
      goToCandidateView('prescreening');
      return;
    }
    goToCandidateView(item.id);
  };

  useEffect(() => {
    if (normalizedUserType !== 'candidate') return undefined;
    let cancelled = false;
    const loadWalkthrough = async () => {
      try {
        const response = await candidateAPI.getProfile();
        const prefs = response.data?.notificationPreferences || {};
        if (!cancelled && !prefs.walkthroughCompleted && !prefs.walkthroughDontShow) {
          setShowWalkthrough(true);
        }
      } catch (error) {
        // Dashboard still works if preferences cannot be loaded.
      }
    };
    loadWalkthrough();
    return () => {
      cancelled = true;
    };
  }, [normalizedUserType]);
  
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center">
        <div className="text-center">
          <div className="spinner h-10 w-10 mx-auto"></div>
          <p className="mt-4 text-secondary-600">Loading...</p>
        </div>
      </div>
    );
  }

  const getDashboardContent = () => {
    switch (normalizedUserType) {
      case 'admin':
        return <AdminDashboard user={user} />;
      
      case 'employer':
        return <EmployerDashboard user={user} />;
      
      case 'candidate':
        return (
          <CandidateDashboard
            user={user}
            candidateView={candidateView}
            setCandidateView={setCandidateView}
            onPrescreeningStatusChange={setPrescreeningStatus}
            onIsTakingAssessmentChange={setIsTakingAssessment}
            pendingAction={candidateAction}
            onPendingActionHandled={() => setCandidateAction(null)}
          />
        );
      
      default:
        return <div>Unknown user type</div>;
    }
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Navigation */}
      <nav className="app-header">
        <div className="px-4 sm:px-6 lg:px-8 relative">
          <div className="flex justify-between items-center h-16 gap-3 min-w-0">
            {/* Left: Menu toggle (small screens) + App Logo */}
            <div className="flex items-center gap-2 shrink-0">
              {candidateNavVisible && (
                <button
                  type="button"
                  onClick={() => setMobileNavOpen(true)}
                  className="btn-ghost lg:hidden"
                  aria-label="Open menu"
                  aria-expanded={mobileNavOpen}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              )}
              <div className="flex items-start gap-2">
                <img
                  src={`${process.env.PUBLIC_URL}/liaizn-logo.png`}
                  alt="Liaizn logo"
                  className="h-10 w-28 object-contain sm:h-14 sm:w-[9.5rem]"
                />
                <span className="badge-primary hidden translate-y-1 px-1.5 py-0.5 text-[10px] leading-none whitespace-nowrap sm:inline-flex">
                  AI-Driven
                </span>
              </div>
            </div>
            {candidateNavVisible && user?.firstName && (
              <p className="hidden min-w-0 flex-1 truncate px-2 text-sm font-medium text-secondary-800 sm:block">
                Welcome, {user.firstName}
              </p>
            )}

            {/* Center: Logo (Employer only) */}
            {normalizedUserType === 'employer' && employerLogoUrl && (
              <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center">
                <img
                  src={employerLogoUrl}
                  alt={`${employerProfile?.companyName || user.companyName || 'Company'} logo`}
                  className="h-16 w-auto max-w-48 rounded-md object-contain border border-gray-200 bg-white flex-shrink-0"
                  style={{ maxHeight: '64px' }}
                  onError={(e) => {
                    console.error('Failed to load company logo:', employerLogoUrl);
                    e.target.style.display = 'none';
                  }}
                  onLoad={() => {
                    console.log('Company logo loaded successfully:', employerLogoUrl);
                  }}
                />
              </div>
            )}

            {/* Right: Notifications + Company Name + Account menu */}
            <div className="flex items-center gap-2 min-w-0 justify-end">
              {candidateNavVisible && (
                <NotificationBell
                  onNavigate={handleCandidateNotification}
                  refreshKey={`${candidateView}-${prescreeningStatus.isCompleted}-${showWalkthrough}`}
                />
              )}
              {/* Company Name (Employer only) */}
              {normalizedUserType === 'employer' && (
                <span className="badge-neutral px-3 py-1.5 text-sm">
                  {employerProfile?.companyName || user.companyName || 'Employer Account'}
                </span>
              )}
              <UserMenu
                user={user}
                onLogout={logout}
              />
            </div>
          </div>
        </div>
      </nav>

      {normalizedUserType === 'candidate' && (
        <ProductWalkthrough
          open={showWalkthrough}
          assessmentsLocked={!prescreeningStatus.isCompleted}
          onComplete={closeWalkthrough}
          showFeedback={!walkthroughIsReplay}
        />
      )}

      {/* Main Content */}
      <div className="flex">
        {candidateNavVisible && (
          <DashboardSidebar
            items={candidateNavItems}
            activeId={candidateActiveNavId}
            onSelect={handleCandidateNavSelect}
            ariaLabel="Candidate sections"
            title="Menu"
            mobileOpen={mobileNavOpen}
            onMobileClose={() => setMobileNavOpen(false)}
            footerItem={candidateProfileNavItem}
          />
        )}
        <div className="min-w-0 flex-1 overflow-x-hidden">
          <div className="mx-auto w-full max-w-7xl sm:px-6 lg:px-8 2xl:max-w-none 2xl:px-10">
            <div className={`px-4 sm:px-0 ${
              normalizedUserType === 'admin'
                ? 'py-6'
                : normalizedUserType === 'candidate'
                  ? (candidateView === 'dashboard' && !courseRouteActive ? 'py-3' : 'py-6')
                  : ''
            }`}>
              <Routes>
                <Route index element={<>{getDashboardContent()}</>} />
                <Route path="candidate/course/:courseId" element={<CandidateCoursePage />} />
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
